/**
 * TV 디스플레이(전광판) 관리 카드 — 2026-06-21
 * 시설 정보 페이지에 들어감. 우리 원 TV 주소 + 재발급 + TV 전용 공지 관리.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../config/api';

interface Notice {
  id: string;
  title: string;
  content: string;
  is_pinned: boolean;
  created_at: string | null;
}

const authHeader = (): Record<string, string> => {
  const t = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken') || localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function DisplayBoardCard() {
  const [token, setToken] = useState<string>('');
  const [notices, setNotices] = useState<Notice[]>([]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [pinned, setPinned] = useState(false);
  const [busy, setBusy] = useState(false);
  // 공지 글씨 크기 (2026-09-06 대표 지시) — TV 크기·거리가 매장마다 달라 직접 고르게 한다
  const [fontScale, setFontScale] = useState<number>(1);
  const [fontSaved, setFontSaved] = useState(false);

  const tvUrl = token ? `${window.location.origin}/display/${token}` : '';

  const loadInfo = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/owner/display/info`, { headers: authHeader() });
      if (res.ok) setToken((await res.json()).token || '');
    } catch { /* noop */ }
  }, []);
  const loadNotices = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/owner/display/notices`, { headers: authHeader() });
      if (res.ok) setNotices((await res.json()).notices || []);
    } catch { /* noop */ }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/owner/display/settings`, { headers: authHeader() });
      if (res.ok) setFontScale(Number((await res.json()).notice_font_scale) || 1);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { loadInfo(); loadNotices(); loadSettings(); }, [loadInfo, loadNotices, loadSettings]);

  const saveFontScale = async (v: number) => {
    const before = fontScale;
    setFontScale(v);                 // 눌린 게 바로 보이게
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/owner/display/settings`, {
        method: 'PUT',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ notice_font_scale: v }),
      });
      if (!res.ok) { setFontScale(before); alert('글씨 크기를 저장하지 못했습니다.'); return; }
      setFontSaved(true);
      setTimeout(() => setFontSaved(false), 2500);
    } catch {
      setFontScale(before);
      alert('글씨 크기를 저장하지 못했습니다.');
    }
  };

  const regenerate = async () => {
    if (!window.confirm('TV 주소를 재발급하면 기존 주소는 즉시 사용할 수 없습니다. 계속할까요?')) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/owner/display/regenerate`, { method: 'POST', headers: authHeader() });
      if (res.ok) { setToken((await res.json()).token || ''); alert('새 TV 주소가 발급되었습니다'); }
    } finally { setBusy(false); }
  };
  const addNotice = async () => {
    if (!title.trim()) { alert('제목을 입력하세요'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/owner/display/notices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ title: title.trim(), content, is_pinned: pinned }),
      });
      if (res.ok) { setTitle(''); setContent(''); setPinned(false); await loadNotices(); }
      else alert('등록 실패');
    } finally { setBusy(false); }
  };
  const deleteNotice = async (id: string) => {
    if (!window.confirm('이 TV 공지를 삭제할까요?')) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/owner/display/notices/${id}`, { method: 'DELETE', headers: authHeader() });
      if (res.ok) await loadNotices();
    } finally { setBusy(false); }
  };
  const copyUrl = () => { if (tvUrl) { navigator.clipboard?.writeText(tvUrl); alert('TV 주소가 복사되었습니다'); } };

  const box: React.CSSProperties = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 16 };

  return (
    <div style={box}>
      <h3 style={{ fontSize: 18, fontWeight: 700, marginTop: 0, marginBottom: 4 }}>📺 TV 디스플레이 (전광판)</h3>
      <p style={{ color: '#6b7280', fontSize: 13, marginTop: 0 }}>
        아래 주소를 우리 원 TV 브라우저에서 열면 공지 + 우리 지점/전체 랭킹이 표시됩니다. 이 주소는 우리 원 전용입니다(다른 원은 볼 수 없음).
      </p>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '12px 0' }}>
        <input readOnly value={tvUrl} style={{ flex: 1, minWidth: 240, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, color: '#374151', background: '#f9fafb' }} />
        <button onClick={copyUrl} disabled={!tvUrl} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' }}>복사</button>
        <a href={tvUrl} target="_blank" rel="noreferrer" style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #2563eb', color: '#2563eb', textDecoration: 'none' }}>미리보기</a>
        <button onClick={regenerate} disabled={busy} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #ef4444', color: '#ef4444', background: '#fff', cursor: 'pointer' }}>재발급</button>
      </div>

      {/* 공지 글씨 크기 (2026-09-06 대표 지시) — 공지에만 적용된다.
          순위 칸은 4개가 격자로 붙어 있어 키우면 이름이 잘리고 칸이 서로 밀린다. */}
      <div style={{ borderTop: '1px solid #f3f4f6', marginTop: 16, paddingTop: 16 }}>
        <h4 style={{ fontSize: 15, fontWeight: 700, margin: 0, marginBottom: 4 }}>공지 글씨 크기</h4>
        <p style={{ color: '#6b7280', fontSize: 13, marginTop: 0, marginBottom: 8 }}>
          TV가 멀리 있으면 크게 하세요. <b>공지사항에만</b> 적용되고 순위 칸은 그대로입니다.
          누르면 바로 저장되고, TV는 5분 안에 저절로 바뀝니다(새로고침하면 즉시).
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {[
            { v: 0.85, label: '작게' },
            { v: 1.0, label: '보통' },
            { v: 1.2, label: '크게' },
            { v: 1.45, label: '아주 크게' },
            { v: 1.7, label: '가장 크게' },
          ].map(o => {
            const on = Math.abs(fontScale - o.v) < 0.01;
            return (
              <button
                key={o.v}
                onClick={() => saveFontScale(o.v)}
                style={{
                  padding: '8px 14px',
                  borderRadius: 8,
                  border: on ? '2px solid #2563eb' : '1px solid #d1d5db',
                  background: on ? '#eff6ff' : '#fff',
                  color: on ? '#1d4ed8' : '#374151',
                  fontWeight: on ? 700 : 500,
                  cursor: 'pointer',
                }}
              >
                {o.label}
              </button>
            );
          })}
          {fontSaved && <span style={{ color: '#16a34a', fontSize: 13, fontWeight: 600 }}>저장했습니다</span>}
        </div>
      </div>

      <div style={{ borderTop: '1px solid #f3f4f6', marginTop: 16, paddingTop: 16 }}>
        <h4 style={{ fontSize: 15, fontWeight: 700, margin: 0, marginBottom: 8 }}>TV 공지 관리</h4>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="공지 제목" style={{ flex: 1, minWidth: 200, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8 }} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#374151' }}>
            <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} /> 상단 고정
          </label>
        </div>
        <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="내용 (선택)" rows={2} style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, marginBottom: 8 }} />
        <button onClick={addNotice} disabled={busy} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>공지 등록</button>

        <div style={{ marginTop: 12 }}>
          {notices.length === 0 ? (
            <div style={{ color: '#9ca3af', fontSize: 13, padding: '8px 0' }}>등록된 TV 공지가 없습니다</div>
          ) : (
            notices.map((n) => (
              <div key={n.id} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{n.is_pinned && <span style={{ color: '#f59e0b' }}>📌 </span>}{n.title}</div>
                  {n.content && <div style={{ fontSize: 13, color: '#6b7280', whiteSpace: 'pre-wrap' }}>{n.content}</div>}
                </div>
                <button onClick={() => deleteNotice(n.id)} disabled={busy} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #fca5a5', color: '#ef4444', background: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' }}>삭제</button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
